import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Role, UserView } from '@geoic/shared';
import { cn } from '@/lib/cn.js';
import { visibleGroups } from '@/app/navigation.js';
import { ROLE_LABELS } from '@/components/ui/Badge.js';
import { initials } from './Topbar.js';

export type SidebarProps = {
  role: Role | undefined;
  user: UserView;
  collapsed: boolean;
  onToggle: () => void;
  /** Ochilmagan arizalar soni — maketda «Arizalar» yonidagi belgi. */
  badges?: Partial<Record<string, number>>;
};

/**
 * Yon menyu — MAKETDAN (`D1 Login va boshqaruv paneli.dc.html`).
 *
 * To'q ko'k fon (`brand-700`), 244px kenglikda; yig'ilganda 62px va
 * faqat ikonkalar qoladi. Faol band brend ko'kida va CHAP chekkasida
 * 3px aksent chiziq bilan belgilanadi — maketda ajratish shu bilan,
 * fon rangi orqali emas.
 *
 * Holat `localStorage` da (`AppShell`) — foydalanuvchi har kirganda
 * qayta yig'ib o'tirmasin.
 */
export function Sidebar({ role, user, collapsed, onToggle, badges }: SidebarProps) {
  const groups = visibleGroups(role);

  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col border-r border-n-900 bg-brand-700 text-onbrand-100 transition-[width] duration-200',
        collapsed ? 'w-[62px]' : 'w-[244px]',
      )}
    >
      <div className="flex h-[62px] items-center justify-between gap-2.5 border-b border-onbrand-line px-4">
        <div className="flex items-center gap-2.5 overflow-hidden">
          <span className="flex size-7 shrink-0 items-center justify-center bg-white">
            <img src="/logo-mark.svg" alt="" className="size-5" />
          </span>
          {!collapsed && (
            <span className="truncate text-base font-bold tracking-[0.03em] whitespace-nowrap text-white">
              GEOIC admin
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Menyuni yoyish' : 'Menyuni yig‘ish'}
          className="flex size-[30px] shrink-0 items-center justify-center border border-onbrand-line text-onbrand-100 hover:bg-brand-600 hover:text-white"
        >
          {collapsed ? (
            <ChevronRight className="size-[15px]" aria-hidden />
          ) : (
            <ChevronLeft className="size-[15px]" aria-hidden />
          )}
        </button>
      </div>

      <nav aria-label="Asosiy menyu" className="flex-1 overflow-y-auto py-2.5">
        {groups.map((group) => (
          <div key={group.title} className="mb-2.5 last:mb-0">
            {/*
              Guruh sarlavhasi yig'ilgan holatda ko'rsatilmaydi — 62px
              enda u qisqartirilib, ma'nosini yo'qotardi.
            */}
            {!collapsed && (
              <p className="px-4 pt-2 pb-1.5 font-mono text-[10.5px] tracking-[0.12em] text-onbrand-500 uppercase">
                {group.title}
              </p>
            )}

            <ul className="flex flex-col gap-px">
              {group.items.map((item) => {
                const badge = badges?.[item.path];

                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/'}
                      title={collapsed ? item.label : undefined}
                      className={({ isActive }) =>
                        cn(
                          'flex h-[42px] items-center gap-3 border-l-[3px] px-[13px] text-[13.5px] font-medium',
                          isActive
                            ? 'border-accent-600 bg-brand-600 text-white'
                            : 'border-transparent text-onbrand-100 hover:bg-brand-600 hover:text-white',
                          collapsed && 'justify-center px-0',
                        )
                      }
                    >
                      <item.icon className="size-[17px] shrink-0" aria-hidden />
                      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                      {!collapsed && badge !== undefined && badge > 0 && (
                        <span className="flex h-5 min-w-5 shrink-0 items-center justify-center bg-accent-600 px-1.5 text-[11px] font-bold tabular text-white">
                          {badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Maketda menyu OSTIDA joriy foydalanuvchi turadi */}
      <div className="flex items-center gap-3 border-t border-onbrand-line px-4 py-3.5">
        <span className="flex size-8 shrink-0 items-center justify-center border border-onbrand-line bg-brand-600 text-xs font-bold text-white">
          {initials(user.fullName)}
        </span>
        {!collapsed && (
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[13px] font-semibold text-white">{user.fullName}</span>
            <span className="truncate text-[11.5px] text-onbrand-500">
              {ROLE_LABELS[user.role]}
            </span>
          </span>
        )}
      </div>
    </aside>
  );
}
